import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { reconciliation, type ReconciliationRow } from './api'
import { ReturnStatusChip, formLabel, seatLabel } from './vocab'

/**
 * Returns whose arithmetic does not hold.
 *
 * ## Detected, never corrected
 *
 * Nothing on this page changes a figure. A return that fails reconciliation is one somebody
 * has to look at with the paper in front of them — a system that "fixed" it would be
 * inventing a number, and the invented number would then be indistinguishable from a counted
 * one.
 *
 * ## Every problem, not the first
 *
 * A return can be over-subscribed AND have a candidate sum that misses. Reporting one at a
 * time means two trips to the paper.
 */
export function ReconciliationPage() {
  const { electionId } = useElectionContext()
  const [rows, setRows] = useState<ReconciliationRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await reconciliation({ electionId, size: 300 }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A reconciliation report" />

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-erp-base font-semibold">Reconciliation</h2>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Returns whose numbers do not add up — more ballots than voters, a total that
          disagrees with its parts, or candidate votes that miss the valid vote.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState
          title="Everything adds up"
          hint="Every filed return's arithmetic holds: ballots cast within the register,
                totals equal to their parts, and candidate votes summing to the valid vote." />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Place</Th>
            <Th>Race</Th>
            <Th>Status</Th>
            <Th className="text-right">Registered</Th>
            <Th className="text-right">Cast</Th>
            <Th className="text-right">Valid</Th>
            <Th className="text-right">Candidate sum</Th>
            <Th>What does not add up</Th>
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={8} />
            : rows.map((r) => (
              <tr key={r.returnId}>
                <Td>{r.unitName ?? '—'}</Td>
                <Td>
                  {seatLabel(r.seatTypeCode)}
                  <span className="block text-erp-xs text-muted-foreground">
                    {formLabel(r.formTypeCode)}
                  </span>
                </Td>
                <Td><ReturnStatusChip code={r.statusCode} /></Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.registeredVoters)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.totalCast)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.validVotes)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.candidateSum)}</Td>
                <Td>
                  <ul className="space-y-0.5">
                    {r.problems.map((p, i) => (
                      <li key={i}
                        className="text-erp-xs text-red-700 dark:text-red-400 flex gap-1.5">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <Note tone="info">
        Nothing here is corrected automatically. A return that does not reconcile is checked
        against the paper, and if the paper says something different the figure is changed by
        filing a <strong>correction</strong> — a new version, with a reason, superseding the
        one that was wrong.
      </Note>
    </div>
  )
}
